import {ReactNode, useEffect, useState} from "react";
import { Box, Card, CardContent, Typography, Avatar, Stack, Skeleton } from "@mui/material";
import { Visibility, Share, AccessTime, Block } from "@mui/icons-material";
import { Grid } from "@mui/material";
import ComponentLoader from "./ComponentLoader";
import {SharedCountDTO} from "../../types/DTOs/Dashboard/SharedCountDTO";
import {DashboardAPI} from "../../services/API/DashboardAPI";

interface KpiItem {
    label: string;
    getValue: (stats: SharedCountDTO) => number;
    sub: (stats: SharedCountDTO) => string;
    icon: ReactNode;
    accent: string;
}

const KPI_CONFIG: KpiItem[] = [
    {
        label: "Share Attive",
        getValue: (s) => s.activeShares,
        sub: (s) => `su ${s.totalShares} totali`,
        icon: <Share />,
        accent: "#6C63FF",
    },
    {
        label: "Visualizzazioni",
        getValue: (s) => s.totalViews,
        sub: () => "su tutti i link",
        icon: <Visibility />,
        accent: "#00D4AA",
    },
    {
        label: "Scadute",
        getValue: (s) => s.expiredShares,
        sub: () => "non più accessibili",
        icon: <AccessTime />,
        accent: "#FFB347",
    },
    {
        label: "Eliminate",
        getValue: (s) => s.deletedShares,
        sub: () => "archiviate",
        icon: <Block />,
        accent: "#FF6B6B",
    },
];

interface KpiCardProps {
    label: string;
    value: number;
    sub: string;
    icon: ReactNode;
    accent: string;
}

function KpiCard({ label, value, sub, icon, accent }: KpiCardProps) {
    return (
        <Card
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                height: "100%",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0, left: 0,
                    width: 4, height: "100%",
                    background: accent,
                    borderRadius: "4px 0 0 4px",
                },
            }}
        >
            <CardContent sx={{ pl: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={1} textTransform="uppercase">
                            {label}
                        </Typography>
                        <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.1, mt: 0.5, fontFamily: "'DM Serif Display', Georgia, serif" }}>
                            {value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            {sub}
                        </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: accent + "22", color: accent, width: 44, height: 44 }}>
                        {icon}
                    </Avatar>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function KpiCards() {
    //const { data,  refetch } = useFetch(fetchShareStats);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<SharedCountDTO>({
        activeShares: 0,
        deletedShares: 0,
        expiredShares: 0,
        totalShares: 0,
        totalViews: 0
    });

    useEffect(() => {
        loadData();
    }, [])

    const loadData = async () =>{
        setLoading(true);
        setError('');
        // caricamento dati
        const countData = await DashboardAPI.Instance<DashboardAPI>().getCountViews();
        setData(countData);

        setLoading(false);
    };

    if (loading) {
        return (
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {KPI_CONFIG.map((k) => (
                    <Grid key={k.label} size={{ xs: 6, md: 3 }}>
                        <Skeleton variant="rounded" height={110} sx={{ borderRadius: 3 }} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {KPI_CONFIG.map((k) => (
                <Grid key={k.label} size={{ xs: 6, md: 3 }}>
                    <ComponentLoader loading={false} error={error} skeletonHeight={110}>
                        {data ? (
                            <KpiCard
                                label={k.label}
                                value={k.getValue(data)}
                                sub={k.sub(data)}
                                icon={k.icon}
                                accent={k.accent}
                            />
                        ) : <></>}
                    </ComponentLoader>
                </Grid>
            ))}
        </Grid>
    );
}